import React, { useState, useEffect, useRef } from 'react';
import Header from '../Header/Header';
import { Icon, CustomTable, Search, Pagination, Loader, ICONS, Select } from '@ohif/ui';
import { useTheme } from "../../ui/src/contextProviders";
import { useSelector, useDispatch } from 'react-redux';
import { AppDispatch, RootState } from '@custom/store/store';
import { customStyle, formatDate, formatTime } from '@custom/Utils/utils';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { DatePickerWithRange } from '@ohif/ui-next';
import { Form, Formik } from 'formik';
import getTeleradiologistPayouts from '@custom/rtk-apis/teleradiologistPayouts/getTeleRadiologistPayouts';
import { resetTeleradiologistPayouts } from '@custom/store/slices/teleradiologistPayouts';

// Add proper type definitions
interface QueryParams {
    hospitalId: string;
    limit: number;
    pageNumber: number;
    search: string;
    dateRange?: string;
    startDate?: string;
    endDate?: string;
    [key: string]: any; // To allow additional dynamic properties
}

interface AuditResponseResult {
    totalCount: number;
    data: Array<any>;
    [key: string]: any;
}

interface AuditResponse {
    status: string;
    result: AuditResponseResult;
    loading: boolean;
    msg: string;
    error: string;
    [key: string]: any;
}

const TeleradiologistPayouts = () => {
    // Use unknown first to safely cast to our custom type
    const teleradiologistPayoutsResponse = useSelector((state: RootState) => state.teleradiologistPayoutsResponse) as unknown as AuditResponse;
    const dispatch = useDispatch<AppDispatch>();

    const { isDarkMode } = useTheme();

    const [searchQuery, setSearchQuery] = useState('');
    const [tableData, setTableData] = useState([]);
    const [pgNumber, setPgNumber] = useState(1);
    const [pgLimit, setPgLimit] = useState(30);
    const [search, setSearch] = useState('');
    const [totalPg, setTotalPg] = useState(100);
    const [searchInputValue, setSearchInputValue] = useState('');
    const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>({
        startDate: null,
        endDate: null,
    });
    const [selectedTimeFrame, setSelectedTimeFrame] = useState(null);

    const columns = [
        { key: 'doctorName', title: 'Doctor Name' }, // radiologist, pathologist
        { key: 'modality', title: 'Modality' }, // Modality Name
        { key: 'studyRegion', title: 'Study Region' }, // Study Region
        { key: 'doctorPayout', title: 'Doctor Payout' },
        { key: 'hospitalPayout', title: 'Hospital Credit' },
        { key: 'finalHospitalPayout', title: 'Hospital Payout' },
    ];
    const actions = [];

    const searchClick = (searchText) => {
        setSearch(searchText);
        setSearchInputValue(searchText);
        fetchAuditData({ search: searchText });
    };

    const onPageChangeHandler = (pg) => {
        setPgNumber(pg);
        fetchAuditData({ pageNumber: pg });
    };

    const handlePageSizeChange = (e) => {
        setPgLimit(e);
        fetchAuditData({ limit: e });
    };

    const resetFilter = () => {
        setSearch('');
        setSearchInputValue('');
        setDateRange({ startDate: '', endDate: '' });
        setSelectedTimeFrame(null);
        setPgNumber(1);
        fetchAuditData({});
    };

    const handleDateChange = (dates: { startDate: string, endDate: string }) => {
        const { startDate, endDate } = dates;
        if (startDate && endDate) {
            setDateRange({ startDate, endDate });
            setSelectedTimeFrame(null); // Clear timeframe when date range is selected
            fetchAuditData({ startDate, endDate });
        }
    };

    const modifyDate = (date?: string) => {
        if (!date) return "";
        // Transform "DDMMYYYY" to "YYYY-MM-DD"
        return `${date?.slice(4, 8)}-${date?.slice(2, 4)}-${date?.slice(0, 2)}`;
    };

    const fetchAuditData = (params = {}) => {
        const hostpialId = sessionStorage.getItem('hospitalId') || localStorage.getItem('hospitalId');
        const queryParams: QueryParams = {
            hospitalId: hostpialId,
            limit: pgLimit,
            pageNumber: pgNumber,
            search: search,
            ...params, // Override with any additional params
        };

        if (Object.keys(params).length > 0 && selectedTimeFrame && !dateRange.startDate && !dateRange.endDate) {
            queryParams.dateRange = selectedTimeFrame.value;
        } else if (queryParams.startDate && queryParams.endDate) {
            const changedStartDate = modifyDate(queryParams.startDate);
            const changedEndDate = modifyDate(queryParams.endDate);
            queryParams.startDate = changedStartDate;
            queryParams.endDate = changedEndDate;
        }

        dispatch(getTeleradiologistPayouts(queryParams));
    };

    const capitalizeWords = (str) =>
        str
          ? str
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ')
          : '';

    useEffect(() => {
        if (teleradiologistPayoutsResponse.status === 'SUCCESS') {
            setTotalPg(teleradiologistPayoutsResponse?.result?.totalCount || 0);
            const tableData = (teleradiologistPayoutsResponse?.result?.data || []).map(item => ({
                ...item,
                doctorName: capitalizeWords(item?.doctorName),
                doctorPayout: `₹ ${item?.doctorPayout}`,
                finalHospitalPayout: `₹ ${item?.hospitalPayout * item?.hospitalCreditMultiplier}`
              }));

              setTableData(tableData);
        }
        return () => {
            dispatch(resetTeleradiologistPayouts());
        }
    }, [teleradiologistPayoutsResponse.status, teleradiologistPayoutsResponse.result]);

    useEffect(() => {
        fetchAuditData(); // Initial fetch
    }, []);

    return (
        <>
            <div className='page page__fullBody' style={isDarkMode ? { background: '#090c29' } : customStyle.mainBackground}>
                <Header isBack={false} title='Tele User Payouts'></Header>
                <div className='page__body'>
                    <div className='tableWrap'>
                        <div className='tableWrap__head'>
                            <div className="formRow formRow__3 m_0 w_100 formRow--flexEnd">
                                <div className='formCol'>
                                    <Search text="Doctor Name" onSearchClick={searchClick} value={searchInputValue} setValue={setSearchInputValue}></Search>
                                </div>
                                <div className='formCol'>
                                    <DatePickerWithRange
                                        id="dateFilter"
                                        className="px-4 py-1 rounded h-10"
                                        onChange={handleDateChange as any}
                                        startPlaceholder="From"
                                        endPlaceholder="To"
                                        startDate={dateRange.startDate}
                                        endDate={dateRange.endDate}
                                        style={{ width: '200px' }}
                                    />
                                </div>
                                <div className="formCol">
                                    <div className='btn__group'>
                                        <button onClick={resetFilter} className="filter-button" style={{ marginLeft: '5px' }}>
                                            <Icon name="ClearFilters" className="text-black cursor-pointer" width="16" height="16"></Icon>
                                        Clear
                                        </button>
                                    </div>
                                </div>
                            </div>
                            {/* <button className='btn btn__white' style={{ borderRadius: '50px' }} onClick={handleDownload}>
                                Download PDF
                            </button> */}
                        </div>
                        <div className='tableContainer'>
                            <CustomTable
                                columns={columns}
                                data={tableData}
                                actions={actions}
                                searchQuery={searchQuery}
                                from="teleradiologistPayouts"
                                userDetails={''}
                                setClickedItem={''}
                                clickedItem={''}
                            />
                        </div>
                    </div>
                </div>
                {tableData.length > 0 && <Pagination
                    onChangePage={onPageChangeHandler}
                    onChangePerPage={handlePageSizeChange}
                    totalPage={totalPg}
                    currentPage={pgNumber}
                    perPage={pgLimit}
                />}
                <Loader isLoaderOpen={teleradiologistPayoutsResponse.loading} />
            </div>
        </>
    );
};

export default TeleradiologistPayouts;
